import React, { createContext, useCallback, useEffect, useRef, useState, useMemo } from 'react';

import { ArToolkitSource, ArToolkitContext } from '@hatsumatsu/ar.js/three.js/build/ar-threex.js';
import { useFrame, useThree } from '@react-three/fiber';

const ARContext = createContext(null);

const AR = ({
    children,
    patternRatio,
    matrixCodeType,
    detectionMode,
    cameraParametersUrl,
    onCameraStreamReady = () => {},
    onContextReady = () => {},
}) => {
    console.log('AR', 'render');

    const { gl, camera } = useThree();

    const [context, setContext] = useState();

    const arToolkitSource = useRef(null);
    const arToolkitContext = useRef(null);

    const initAR = useCallback(() => {
        console.log('AR.initAR()', camera);

        // SOURCE
        arToolkitSource.current = new ArToolkitSource({
            sourceType: 'webcam',
            // creates a landscape webcam stream no matter what the device orientation is...
            sourceWidth: window.innerWidth > window.innerHeight ? 640 : 480,
            sourceHeight: window.innerWidth > window.innerHeight ? 480 : 640,
        });

        arToolkitSource.current.init(() => {
            if (!arToolkitSource.current) {
                return;
            }

            arToolkitSource.current.domElement.addEventListener('canplay', () => {
                // check if source was disposed in the meantime...
                if (!arToolkitSource.current) {
                    return;
                }

                console.log(
                    'arToolkitSource',
                    'canplay',
                    'actual source dimensions',
                    `${arToolkitSource.current.domElement.videoWidth} x ${arToolkitSource.current.domElement.videoHeight}`
                );

                onCameraStreamReady(arToolkitSource.current, arToolkitContext.current, gl);

                // CONTEXT
                arToolkitContext.current = new ArToolkitContext({
                    cameraParametersUrl,
                    detectionMode,
                    patternRatio,
                    matrixCodeType,
                });

                // this creates a memory leak
                arToolkitContext.current.init(() => {
                    // check if context was disposed in the meantime...
                    if (!arToolkitContext.current || !camera) {
                        return;
                    }
                    camera.projectionMatrix.copy(arToolkitContext.current.getProjectionMatrix());
                    console.log('arToolkitContext', arToolkitContext.current);
                    setContext(arToolkitContext.current);

                    document.documentElement.classList.add('ready--ar');
                    if (!arToolkitSource.current || !arToolkitContext.current) {
                        return;
                    }

                    onContextReady(arToolkitSource.current, arToolkitContext.current, gl);
                    camera.updateProjectionMatrix();
                });
            });
        });
    }, [
        cameraParametersUrl,
        detectionMode,
        patternRatio,
        matrixCodeType,
        gl,
        camera,
        onCameraStreamReady,
        onContextReady,
    ]);

    const disposeAR = () => {
        console.log('AR.disposeAR()', arToolkitContext.current, arToolkitSource.current, context);

        // SOURCE
        if (arToolkitSource.current) {
            arToolkitSource.current.dispose();
        }
        arToolkitSource.current = null;

        // CONTEXT
        if (arToolkitContext.current) {
            arToolkitContext.current.dispose();
        }

        arToolkitContext.current = null;

        setContext(null);
        document.documentElement.classList.remove('ready--ar');

        console.log('AAA', arToolkitSource.current, arToolkitContext.current, context);
    };

    useEffect(() => {
        initAR();

        return () => {
            disposeAR();
        };
    }, []);

    // resize
    useEffect(() => {
        let resizeDelay;

        function onResize() {
            disposeAR();
            initAR();
        }

        function handleResize() {
            if (resizeDelay) {
                clearTimeout(resizeDelay);
            }

            resizeDelay = setTimeout(() => {
                onResize();
            }, 1000);
        }

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useFrame(() => {
        if (!arToolkitContext.current || !arToolkitSource.current?.domElement) {
            return;
        }

        arToolkitContext.current.update(arToolkitSource.current.domElement);
    });

    return <ARContext.Provider value={context}>{children}</ARContext.Provider>;
};

const useAR = () => {
    const _context = React.useContext(ARContext);
    return React.useMemo(() => _context, [_context]);
};

export { AR, useAR };
